/**
 * The demo's first-party attribution: a fixed set of conversion paths, each with the
 * touch every model credits and a daily rate of purchases, summed over whatever range,
 * model and grouping the page asks for. Each path's sales go to exactly one row under
 * each model, so — as on the API (attribution.md §4) — every model totals the same and
 * only the credit moves. One path has no touch at all (the Not tracked row, `credit:
 * 'unattributed'`, never merged with direct); two end on a direct visit, so last touch
 * and last non-direct disagree about them; one's paid click is inside the 7-day click
 * window, so paid click credits it over a later organic visit. Days before the tenant's
 * tracking baseline have no tracked touch, so every sale on them is Not tracked too.
 *
 * Meta ids resolve only while the demo's Meta connection feeds a website, as the real
 * fact resolves them only for an imported hierarchy; spend is then the demo's own per-ad
 * daily rate, Meta's figure, never folded into the TmT metrics.
 */

import {
  META_GROUP_BYS,
  type AttributionGroupBy,
  type AttributionMetrics,
  type AttributionQuery,
  type AttributionReport,
  type AttributionRow,
} from '@/api/attribution'
import type { ConversionCredit } from '@/api/dataQuality'
import type { AttributionModel } from '@/api/types'
import { notTrackedLast } from '@/components/dataQuality'
import { addDays, dayCount } from '@/lib/filters'
import { demoDataQuality, trackedDays } from '@/mock/dataQuality'

import { ZERO_METRICS } from './definitions'

/** The API's `definitions.LOOKBACK`. */
const LOOKBACK_DAYS = 30
/** The previous period runs at this share of the current one. */
const PREVIOUS_FACTOR = 0.9

interface DemoTouch {
  /** Absent on every tracked touch; only `NONE` says `'unattributed'`. */
  credit?: 'touch'
  campaign: string | null
  source: string | null
  medium: string | null
  adCampaignId?: string
  adSetId?: string
  adId?: string
}

/** A direct visit: no source, medium or campaign — the null-id `touch` row. */
const DIRECT: DemoTouch = { campaign: null, source: null, medium: null }
/** No tracked touch at all: the Not tracked row — told apart from `DIRECT` by its credit, never by identity. */
const NONE = { credit: 'unattributed' } as const
/** What a model credits a path's sale to: a touch, or nothing tracked. */
type DemoCredited = DemoTouch | typeof NONE

const FB_VIDEO: DemoTouch = {
  campaign: 'summer-sellout',
  source: 'facebook',
  medium: 'paid_social',
  adCampaignId: 'adcmp_demo_summer',
  adSetId: 'adset_demo_uk',
  adId: 'ad_demo_video',
}
const IG_STORY: DemoTouch = {
  campaign: 'summer-sellout',
  source: 'instagram',
  medium: 'paid_social',
  adCampaignId: 'adcmp_demo_summer',
  adSetId: 'adset_demo_uk',
  adId: 'ad_demo_story',
}
const FB_RETARGET: DemoTouch = {
  campaign: 'autumn-draw',
  source: 'facebook',
  medium: 'paid_social',
  adCampaignId: 'adcmp_demo_autumn',
  adSetId: 'adset_demo_retarget',
  adId: 'ad_demo_retarget',
}
const GOOGLE_BRAND: DemoTouch = { campaign: 'brand', source: 'google', medium: 'cpc' }
const GOOGLE_ORGANIC: DemoTouch = { campaign: null, source: 'google', medium: 'organic' }
const NEWSLETTER: DemoTouch = { campaign: 'october-newsletter', source: 'newsletter', medium: 'email' }
const TIKTOK: DemoTouch = { campaign: 'autumn-draw', source: 'tiktok', medium: 'cpc' }

interface DemoPath {
  credited: Record<AttributionModel, DemoCredited>
  purchasesPerDay: number
  aovMinor: number
  newCustomerRate: number
  refundRate: number
}

/** A path credited to one touch under every model, apart from the models named in `differs`. */
function path(
  touch: DemoCredited,
  purchasesPerDay: number,
  aovMinor: number,
  differs: Partial<Record<AttributionModel, DemoCredited>> = {},
): DemoPath {
  return {
    credited: { first_touch: touch, last_touch: touch, last_non_direct: touch, paid_click: touch, ...differs },
    purchasesPerDay,
    aovMinor,
    newCustomerRate: 0.35,
    refundRate: 0.04,
  }
}

const PATHS: readonly DemoPath[] = [
  path(FB_VIDEO, 2.4, 2400),
  path(FB_VIDEO, 1.0, 2200, { last_touch: DIRECT }),
  path(NEWSLETTER, 0.8, 1900, { first_touch: GOOGLE_BRAND }),
  // The Instagram click is inside the 7-day click window: paid click credits it over the later organic visit.
  path(GOOGLE_ORGANIC, 0.7, 2100, { first_touch: IG_STORY, paid_click: IG_STORY }),
  path(TIKTOK, 0.4, 1500),
  path(GOOGLE_ORGANIC, 0.6, 1700, { last_touch: DIRECT }),
  path(GOOGLE_BRAND, 0.5, 2000, { first_touch: FB_RETARGET }),
  // No touch in the lookback: the unattributed row under every model.
  path(NONE, 0.35, 1500),
]

const META_NAMES: Record<string, string> = {
  adcmp_demo_summer: 'Summer sell-out',
  adcmp_demo_autumn: 'Autumn draw',
  adset_demo_uk: 'UK 25-54',
  adset_demo_retarget: 'Retargeting',
  ad_demo_video: 'Video A',
  ad_demo_story: 'Story 1',
  ad_demo_retarget: 'Retargeting carousel',
}

/** Meta's demo spend per ad per day, minor units; rolled up to the ad set and campaign like the API's. */
const AD_SPEND_PER_DAY: ReadonlyArray<{ ad: DemoTouch; minor: number }> = [
  { ad: FB_VIDEO, minor: 3000 },
  { ad: IG_STORY, minor: 1200 },
  { ad: FB_RETARGET, minor: 800 },
]

/** The touch's group at `groupBy` as (id, key columns); null id for the all-null row, as the API's `_identity`. */
function groupOf(touch: DemoTouch, groupBy: AttributionGroupBy, metaActive: boolean): { id: string | null; key: Record<string, string | null> } {
  switch (groupBy) {
    case 'campaign':
      return { id: touch.campaign, key: { campaign: touch.campaign } }
    case 'source_medium': {
      const id = touch.source === null && touch.medium === null ? null : `${touch.source ?? '(none)'} / ${touch.medium ?? '(none)'}`
      return { id, key: { source: touch.source, medium: touch.medium } }
    }
    case 'ad_campaign': {
      const id = metaActive ? (touch.adCampaignId ?? null) : null
      return { id, key: { ad_campaign_id: id } }
    }
    case 'ad_set': {
      const id = metaActive ? (touch.adSetId ?? null) : null
      return { id, key: { ad_set_id: id } }
    }
    case 'ad': {
      const id = metaActive ? (touch.adId ?? null) : null
      return { id, key: { ad_id: id } }
    }
  }
}

function pathMetrics(demo: DemoPath, days: number, factor: number): AttributionMetrics {
  const purchases = Math.round(demo.purchasesPerDay * days * factor)
  const refunds = Math.round(purchases * demo.refundRate)
  return {
    purchases,
    revenueMinor: purchases * demo.aovMinor,
    newCustomers: Math.round(purchases * demo.newCustomerRate),
    refunds,
    refundMinor: refunds * demo.aovMinor,
  }
}

function add(a: AttributionMetrics, b: AttributionMetrics): AttributionMetrics {
  return {
    purchases: a.purchases + b.purchases,
    revenueMinor: a.revenueMinor + b.revenueMinor,
    newCustomers: a.newCustomers + b.newCustomers,
    refunds: a.refunds + b.refunds,
    refundMinor: a.refundMinor + b.refundMinor,
  }
}

interface PeriodRow {
  id: string | null
  key: Record<string, string | null> | null
  credit: ConversionCredit
  metrics: AttributionMetrics
}

interface Period {
  /** By `rowKey`: a group's id, the null-id touch row, or the Not tracked row. */
  rows: Map<string, PeriodRow>
  spend: Map<string, number>
}

const NOT_TRACKED_KEY = 'not-tracked'

function rowMapKey(id: string | null, credit: ConversionCredit): string {
  if (credit === 'unattributed') return NOT_TRACKED_KEY
  return id === null ? 'null' : `id:${id}`
}

/** One period's rows: each path's sales on tracked days to the touch the model credits, the rest Not tracked. */
function period(query: AttributionQuery, days: number, tracked: number, factor: number, metaActive: boolean): Period {
  const rows: Period['rows'] = new Map()
  const credit = (entry: Omit<PeriodRow, 'metrics'>, metrics: AttributionMetrics) => {
    const mapKey = rowMapKey(entry.id, entry.credit)
    const current = rows.get(mapKey)
    rows.set(mapKey, { ...entry, metrics: current ? add(current.metrics, metrics) : metrics })
  }
  const notTracked = { id: null, key: null, credit: 'unattributed' as const }
  for (const demo of PATHS) {
    const touch = demo.credited[query.model]
    const onTracked = pathMetrics(demo, tracked, factor)
    if (touch.credit === 'unattributed') credit(notTracked, onTracked)
    else credit({ ...groupOf(touch, query.groupBy, metaActive), credit: 'touch' }, onTracked)
    if (days > tracked) credit(notTracked, pathMetrics(demo, days - tracked, factor))
  }
  const spend: Period['spend'] = new Map()
  if (META_GROUP_BYS.has(query.groupBy) && metaActive) {
    for (const { ad, minor } of AD_SPEND_PER_DAY) {
      const { id } = groupOf(ad, query.groupBy, metaActive)
      if (id !== null) spend.set(id, (spend.get(id) ?? 0) + Math.round(minor * days * factor))
    }
  }
  return { rows, spend }
}

function total(values: Iterable<AttributionMetrics>): AttributionMetrics {
  let sum = ZERO_METRICS
  for (const value of values) sum = add(sum, value)
  return sum
}

/**
 * The demo's answer to `GET /analytics/attribution/conversions`, shaped as the API's: rows
 * largest revenue first, the null-id touch row after them and the Not tracked row last.
 */
export function demoAttribution(
  query: AttributionQuery,
  currency: string,
  metaActive: boolean,
  baseline: string | null = null,
): AttributionReport {
  const days = dayCount(query.from, query.to)
  const meta = META_GROUP_BYS.has(query.groupBy)
  const previousStart = addDays(query.from, -days)
  const previousEnd = addDays(query.from, -1)
  const current = period(query, days, trackedDays(query.from, query.to, baseline), 1, metaActive)
  const before = query.compare
    ? period(query, days, trackedDays(previousStart, previousEnd, baseline), PREVIOUS_FACTOR, metaActive)
    : null
  const keys = new Set<string>([
    ...current.rows.keys(),
    ...[...current.spend.keys()].map((id) => rowMapKey(id, 'touch')),
    ...(before ? [...before.rows.keys(), ...[...before.spend.keys()].map((id) => rowMapKey(id, 'touch'))] : []),
  ])
  const rows: AttributionRow[] = [...keys].map((mapKey) => {
    const entry = current.rows.get(mapKey) ?? before?.rows.get(mapKey)
    const id = entry ? entry.id : mapKey.slice('id:'.length)
    const credit: ConversionCredit = mapKey === NOT_TRACKED_KEY ? 'unattributed' : 'touch'
    const spendOf = (p: Period) => (id === null ? 0 : (p.spend.get(id) ?? 0))
    return {
      id,
      name: id !== null && meta ? (META_NAMES[id] ?? null) : id,
      key: entry ? entry.key : {},
      credit,
      model: query.model,
      tmtAttributed: true,
      metrics: current.rows.get(mapKey)?.metrics ?? ZERO_METRICS,
      previous: before ? (before.rows.get(mapKey)?.metrics ?? ZERO_METRICS) : null,
      spendMinor: meta ? spendOf(current) : null,
      previousSpendMinor: meta && before ? spendOf(before) : null,
    }
  })
  rows.sort(
    (a, b) =>
      Number(a.id === null) - Number(b.id === null) ||
      b.metrics.revenueMinor - a.metrics.revenueMinor ||
      b.metrics.purchases - a.metrics.purchases ||
      (a.id ?? '').localeCompare(b.id ?? ''),
  )
  const spendTotal = (p: Period) => [...p.spend.values()].reduce((sum, value) => sum + value, 0)
  const totals = total([...current.rows.values()].map((entry) => entry.metrics))
  return {
    start: query.from,
    end: query.to,
    model: query.model,
    groupBy: query.groupBy,
    compared: query.compare,
    previousStart: query.compare ? previousStart : null,
    previousEnd: query.compare ? previousEnd : null,
    currency,
    lookbackDays: LOOKBACK_DAYS,
    tmtAttributed: true,
    spendSource: meta ? 'meta' : null,
    rows: notTrackedLast(rows),
    totals,
    totalsPrevious: before ? total([...before.rows.values()].map((entry) => entry.metrics)) : null,
    totalSpendMinor: meta ? spendTotal(current) : null,
    totalPreviousSpendMinor: meta && before ? spendTotal(before) : null,
    dataQuality: demoDataQuality({
      baseline,
      from: query.from,
      previousFrom: query.compare ? previousStart : null,
      orders: totals.purchases,
      notTrackedOrders: current.rows.get(NOT_TRACKED_KEY)?.metrics.purchases ?? 0,
    }),
  }
}
